import React from "react";
import * as yup from "yup";
import cardboardRemoval from "@assets/pricing/cardboard-removal.svg";
import junkRemoval from "@assets/pricing/junk-removal.svg";
import dumpsterRental from "@assets/pricing/dumpster-rental.svg";
import powerWashing from "@assets/pricing/power-washing.svg";
import Upcoming from "@assets/customer_portal/upcoming_group.svg";
import Review from "@assets/customer_portal/review_group.svg";
import Completed from "@assets/customer_portal/completed_group.svg";
import Cancelled from "@assets/raccoon-tree.png";
import Quotes from "@assets/customer_portal/quotes_group.svg";
import { isValidPhoneNumber } from "react-phone-number-input";

export const bookingConfig = {
  businessHours: {
    earliest: {
      hour: 8,
      minute: 0,
    },
    latest: {
      hour: 20,
      minute: 0,
    },
  },
  businessTime: {
    hour: 6,
    minute: 0,
  },
  dumpsterBusinessTiming: {
    startingTime: [9, 12, 15],
    timeGap: 4,
  },
  getTasksDelay: 15,
};

export const taskTypes = {
  "junk-removal": {
    label: "Junk Removal",
    value: "junk-removal",
    icon: <img src={junkRemoval} alt="J-R" width="20px" height="20px" />,
    default: true,
  },
  "cardboard-removal": {
    label: "Cardboard Removal",
    value: "cardboard-removal",
    icon: <img src={cardboardRemoval} alt="C-R" width="20px" height="20px" />,
    default: true,
  },
  "dumpster-rental": {
    label: "Dumpster Rental",
    value: "dumpster-rental",
    icon: <img src={dumpsterRental} alt="D-R" width="20px" height="20px" />,
    default: true,
  },
  "power-washing": {
    label: "Power Washing",
    value: "power-washing",
    icon: <img src={powerWashing} alt="P-W" width="20px" height="20px" />,
  },
};

export const urlTaskMapping = {
  cardboard_removal: "cardboard-removal",
  junk_removal: "junk-removal",
  dumpster_rental: "dumpster-rental",
  power_washing: "power-washing",
};

export const taskScheduleFrequency = {
  once: {
    label: "Once",
    value: "once",
  },
  weekly: {
    label: "Weekly",
    value: "weekly",
  },
  monthly: {
    label: "Monthly",
    value: "monthly",
  },
};

export const adminTaskScheduleFrequency = {
  ...taskScheduleFrequency,
  "bi-weekly": {
    label: "Biweekly",
    value: "bi-weekly",
  },
};

export const recurringTaskScheduleFrequency = Object.values(adminTaskScheduleFrequency).filter((fr) => fr.value !== taskScheduleFrequency.once.value);

export const bookingStatus = {
  open: {
    label: "Not Started",
    value: "open",
    nextActionLabel: "",
    color: "#c9b49d",
  },
  booked: {
    label: "Available",
    value: "booked",
    color: "#6ccc73",
    nextActionLabel: "Finding a warrior",
  },
  current: {
    label: "Booked",
    value: "current",
    color: "#58b2d6",
    nextActionLabel: "Warrior Matched",
  },
  review: {
    label: "In Review",
    value: "review",
    color: "#f5dc6c",
    nextActionLabel: "Waiting for review",
    nextActionText: "Click to review",
  },
  done: {
    label: "Completed",
    value: "done",
    color: "#009e22",
    nextActionLabel: "Completed",
  },
  cancelled: {
    label: "Cancelled",
    value: "cancelled",
    color: "#ff5e8c",
    nextActionLabel: "",
  },
  needs_approval: {
    label: "Needs Approval",
    value: "needs_approval",
    color: "#ff9100",
    nextActionLabel: "",
  },
  rejected: {
    label: "Rejected by admin",
    value: "rejected",
    color: "#dd2c00",
    nextActionLabel: "",
  },
  pending: {
    label: "Pending Confirmation",
    value: "pending",
    color: "#b8b8b8",
    nextActionLabel: "",
  },
  incomplete: {
    label: "Incomplete",
    value: "incomplete",
    color: "#ca97de",
    nextActionLabel: "",
  },
  expired: {
    label: "Expired",
    value: "expired",
    color: "red",
    nextActionLabel: "",
  },
  failed: {
    label: "Failed",
    value: "failed",
    color: "red",
    nextActionLabel: "",
  },
};

export const bookingBidStatus = {
  available: {
    label: "Available",
    value: "available",
    color: "#FDD835", // yellow
    backgroundColor: "#FFFDE7",
    textColor: "#4c4c4c",
    nextActionLabel: "",
  },
  ["no-warrior-selected"]: {
    label: "No Warrior Selected",
    value: "no-warrior-selected",
    color: "#FFA726", // orange
    backgroundColor: "#FFF3E0",
    nextActionLabel: "Reopen Bid",
  },
  completed: {
    label: "Completed",
    value: "completed",
    color: "#66BB6A", // green
    backgroundColor: "#E8F5E9",
    nextActionLabel: "",
  },
  cancelled: {
    label: "Cancelled",
    value: "cancelled",
    color: "#EF5350", // red
    backgroundColor: "#FFEBEE",
    nextActionLabel: "",
  },
  failed: {
    label: "Failed",
    value: "failed",
    color: "#EF5350", // red
    backgroundColor: "#FFEBEE",
    nextActionLabel: "",
  },
};

export const bookingQuoteStatus = {
  draft: {
    label: "Draft",
    value: "draft",
    color: "darkgray",
    backgroundColor: "white",
    nextActionLabel: "",
  },
  available: {
    label: "Available",
    value: "available",
    color: "#FDD835", // yellow
    backgroundColor: "#FFFDE7",
    textColor: "#4c4c4c",
    nextActionLabel: "",
  },
  completed: {
    label: "Completed",
    value: "completed",
    color: "#66BB6A", // green
    backgroundColor: "#E8F5E9",
    nextActionLabel: "",
  },
  cancelled: {
    label: "Cancelled",
    value: "cancelled",
    color: "#EF5350", // red
    backgroundColor: "#FFEBEE",
    nextActionLabel: "",
  },
  failed: {
    label: "Failed",
    value: "failed",
    color: "#EF5350", // red
    backgroundColor: "#FFEBEE",
    nextActionLabel: "",
  },
};

export const smsAlerts = {
  priceRange: {
    "<100": {
      label: "Less than $100",
      value: "<100",
    },
    "100-200": {
      label: "$100 - $200",
      value: "100-200",
    },
    ">200": {
      label: "More than $200",
      value: ">200",
    },
  },
  distanceRange: {
    "10-miles": {
      label: "10 miles",
      value: "10",
    },
    "25-miles": {
      label: "25 miles",
      value: "25",
    },
    "50-miles": {
      label: "50 miles",
      value: "50",
    },
    "200-miles": {
      label: "200 miles",
      value: "200",
    },
  },
  defaults: {
    priceRange: ["<100", "100-200", ">200"],
    distanceRange: "200",
    taskAvailable: ["cardboard-removal", "junk-removal"],
    vehicleAvailable: false,
    helperAvailable: false,
    alerts: [],
  },
};

export const confirmationPageSurevyOptions = [
  { label: "Google Search", value: "Google Search" },
  { label: "Google Ads", value: "Google Ads" },
  { label: "Yelp Search", value: "Yelp Search" },
  { label: "Facebook / Instagram Ads", value: "Facebook / Instagram Ads" },
  { label: "Bing", value: "Bing" },
  { label: "Linkedin", value: "Linkedin" },
  { label: "Email", value: "Email" },
  { label: "Someone referred me", value: "Someone referred me" },
  { label: "I am a returning customer", value: "I am a returning customer" },
  { label: "Other", value: "Other" },
];

export const vehicleTypes = {
  SUV: {
    label: "SUV",
    value: "SUV",
    disabled: true,
  },
  truck: {
    label: "Truck",
    value: "truck",
  },
  "pick-up-truck": {
    label: "Pick - Up Truck",
    value: "pick-up-truck",
  },
};

export const bidLengths = [
  {
    value: 1,
    label: "1 Day",
  },
  {
    value: 2,
    label: "2 Days",
  },
  {
    value: 3,
    label: "3 Days",
  },
];

export const powerwashItems = ["Driver way or garage", "Building siding", "Deck, patio or porch", "Sidewalk", "Roof"];

const isPowerWashing = (task) => task === taskTypes["power-washing"].value;

const isDumpsterRental = (task) => task === taskTypes["dumpster-rental"].value;

export const adminBookingFormSchema = ({ mode, isNew, isQuoteFromBid, userType }) => {
  const isBid = mode === "booking-bids";
  const isQuote = mode === "booking-quotes";
  const isBooking = mode === "booking";
  // Different schemas for bids vs quotes vs bookings
  return yup.object().shape({
    task: yup.string().required(),
    description: yup.string().optional(),
    status: yup.string().required(),

    frequency: yup.string().required(),
    date: yup
      .date()
      .optional()
      .when("task", { is: () => isBooking, then: yup.date().required() }),
    dateFlexible: yup.boolean().optional().nullable(),
    time: yup
      .object()
      .optional()
      .when("task", { is: () => isBooking, then: yup.object().required() }),

    // dumpster-rental specific
    pickup: yup.string().when("task", { is: isDumpsterRental, then: yup.string().required("pickup type is required") }),
    "pickup-date": yup.object().when("task", { is: isDumpsterRental, then: yup.object().required("pickup date is required") }),
    "pickup-time": yup.object().when("task", { is: isDumpsterRental, then: yup.object().required("pickup time is required") }),
    vehicle: yup.string().when("task", {
      is: (task) => !isDumpsterRental(task) && isBooking,
      then: yup.string().required("pricing option is required"),
    }),

    address: yup.object().shape({
      location: yup.string().required("Invalid Location !"),
      coordinates: yup.object().required("Cannot find coordinates"),
      placeId: yup.string().optional(),
      zipCode: yup.string().optional(),
      city: yup.string().optional(),
    }),
    address_aditional: yup.string().optional(),
    onsite: yup.object().shape({
      instructions: yup.string().optional(),
      images: yup.array().optional().nullable(),
      hours: yup.string().optional(),
      name: yup.string().optional(),
      phone_number: yup
        .string()
        .optional()
        .nullable()
        .test("phone-validation", "Invalid format!", (value) => (value && isValidPhoneNumber(value)) || !value),
      email: yup.string().optional(),
    }),

    phone_number: yup
      .string()
      .required("Phone Numer is required")
      .test("phone-validation", "Invalid format!", (value) => value && isValidPhoneNumber(value)),

    total: yup
      .number()
      .nullable()
      .when("task", {
        is: () => isBooking || isQuoteFromBid,
        then: yup
          .number("Invalid number")
          .typeError("Amount can't be empty!")
          .positive("Amount is invalid!")
          .required()
          .when("task", { is: () => isBid, then: yup.number().optional() }),
      }),

    // Payout and payout_dynamic end up being the same via this form
    payout_dynamic: yup
      .number()
      .nullable()
      .when("task", {
        is: () => (isBooking || isQuoteFromBid) && userType !== "superadmin",
        then: yup
          .number("Invalid number")
          .required()
          .typeError("Amount can't be empty!")
          .positive("Amount is invalid !")
          .lessThan(yup.ref("total"), "Must be less than Customer total")
          .test("is-decimal", "Invalid decimal", (value) => /^\d*[.{1}\d*]\d*$/.test(Number(value))),
      })
      .when("task", {
        is: () => (isBooking || isQuoteFromBid) && userType === "superadmin",
        then: yup
          .number("Invalid number")
          .required()
          .typeError("Amount can't be empty!")
          .positive("Amount is invalid !")
          .test("is-decimal", "Invalid decimal", (value) => /^\d*[.{1}\d*]\d*$/.test(Number(value))),
      }),

    // Payout and payout_dynamic end up being the same via this form
    warriorBidPrice: yup.number().nullable(),

    products: yup.array().when("task", { is: !isDumpsterRental && !isPowerWashing, then: yup.array().required() }),
    services: yup.array().when("task", { is: isDumpsterRental, then: yup.array().required() }),

    stairs: yup.number().when("task", { is: !isDumpsterRental, then: yup.number("Invalid number").positive("Count invalid").required().min(0) }),
    dismantling: yup.number().when("task", { is: !isDumpsterRental, then: yup.number("Invalid number").positive("Count invalid").required().min(0) }),

    coupon: yup.string().optional(),
    // payment_method_id gets adopted from this if it is not ACH
    payment_method: yup
      .string()
      .optional()
      .nullable()
      .when("task", { is: () => isBooking, then: yup.string().required() }),
    stripePaymentMethodId: yup.string().optional().nullable(), // this field is uneditable
    paid: yup
      .boolean()
      .nullable()
      .optional()
      .when("task", { is: () => isBooking, then: yup.boolean().required() }),

    businessClient: yup.bool().required(),
    businessClientDetails: yup
      .object()
      .when("businessClient", {
        is: (businessClient) => businessClient,
        then: yup.object().shape({
          name: yup.string().optional(),
          id: yup.string().optional(),
        }),
      })
      .nullable(),

    // Closed By gets pushed into businessClientDetails on the submit
    closedBy: yup.string().when("businessClientDetails", {
      is: (businessClientDetails) => !!businessClientDetails?.name,
      then: yup.string().required("Closed by is required"),
    }),

    first_name: yup.string().required("Must enter first name"),
    last_name: yup.string().optional(),
    email: yup.string().email().required(),
    customer_id: yup.string().optional().nullable(),

    warrior_id: yup.string().when("task", {
      is: (task) => isPowerWashing(task),
      then: yup.string().required("Warrior is required"),
    }),

    // This is an outdated field
    invoice: yup.string().optional().nullable(),
    invoices: yup.array().optional().nullable(),

    gclId: yup.string().nullable().optional(),
    gclId_intercom: yup.string().nullable().optional(),

    taskImages: yup.array().optional(),
    afterImages: yup.array().optional().nullable(),
    beforeImages: yup.array().optional().nullable(),
    dumpingImages: yup.array().optional().nullable(),

    notes: yup.string().optional(),

    waterSource: yup.boolean().when("task", { is: isPowerWashing, then: yup.boolean().required() }),
    parkingAccess: yup.boolean().when("task", { is: isPowerWashing, then: yup.boolean().required() }),
    powerwashingAreas: yup.array().when("task", { is: isPowerWashing, then: yup.array().optional() }),

    // Bid only
    warriorBidId: yup
      .string()
      .optional()
      .when("task", { is: () => isQuoteFromBid, then: yup.string().required() })
      .nullable(),

    bidLengthValue: yup.number().optional().nullable(),

    bidLengthType: yup.string().optional().nullable(),

    // New field to lock price
    isVehicleFeeWaived: yup.boolean().optional().nullable(),
    isServiceFeeWaived: yup.boolean().optional().nullable(),
    lockPrice: yup.boolean().optional().nullable(),
    lockDate: yup.boolean().optional().nullable(),

    // Auto Select
    isAutoSelectOn: yup.boolean().optional(),
  });
};

export const warriorPayoutStatus = {
  ERROR: "error",
  NOT_STARTED: "not-started",
  INITIATE: "initiate",
  PAUSED: "paused",
  DONE: "done",
};

export const payoutStatusLabel = {
  [warriorPayoutStatus.ERROR]: "Error",
  [warriorPayoutStatus.NOT_STARTED]: "Not Started",
  [warriorPayoutStatus.INITIATE]: "Initiated",
  [warriorPayoutStatus.PAUSED]: "Paused",
  [warriorPayoutStatus.DONE]: "Done",
};

export const customerTypes = [
  {
    label: "Customer",
    value: false,
  },
  {
    label: "Business",
    value: true,
  },
];

export const sameDayBookingPrice = 1000; //$10
export const warriorEarlyClaimBonus = 1000;

export const bookingStatusNoData = {
  active: {
    title: "No upcoming jobs",
    image: Upcoming,
    subtext: "Book a new job to see your upcoming jobs here.",
    actionName: "Book now",
    actionUrl: "booking?step=initial",
  },
  review: {
    title: "No jobs need review",
    image: Review,
    subtext: "Jobs will appear here for you to review shortly after your trash gets picked up.",
    actionName: "",
    actionUrl: "",
  },
  done: {
    title: "No completed jobs",
    image: Completed,
    subtext: "Jobs that you have reviewed will appear here.",
    actionName: "",
    actionUrl: "",
  },
  cancelled: {
    title: "No cancelled jobs",
    image: Cancelled,
    subtext: "Jobs that you cancel will appear here.",
    actionName: "",
    actionUrl: "",
  },
  quotes: {
    title: "No quotes",
    image: Quotes,
    subtext: "Have a custom order? Any quotes you request from Dimension will appear here.",
    actionName: "",
    actionUrl: "",
  },
};
